import { useCallback, useState } from 'react'
import { useMultiValueToggle } from 'hooks/useToggle'
import { EmptyArray } from 'lib/array/arrayUtils'
import type {
  AITripPreferenceStep,
  TripPaceOption,
  ProximityOption,
  ActivityBudgetOption,
  FoodStyleOption,
  DietaryRequirementOption,
  OccupancyOption,
  DatePickerOption,
  Locations,
} from '../types'
import { ISO_DATE_REGEX, isValidDate } from 'lib/datetime/dateUtils'
import {
  calculateTripDays,
  distributeDaysEvenly,
  updateLocationsWithDurations,
  updateLocationDuration,
} from 'tripPlanner/utils/dateDistribution'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'

function validateIsoDateFormat(dateString: string): boolean {
  if (!ISO_DATE_REGEX.test(dateString)) {
    return false
  }
  const date = new Date(dateString + 'T00:00:00')
  return isValidDate(date)
}

interface InitialStateProps {
  startDate?: string
  endDate?: string
  occupancy?: OccupancyOption
}

export function useGenerateTripState(props: InitialStateProps = {}) {
  const { startDate: initialStartDate, endDate: initialEndDate, occupancy: initialOccupancy } = props
  const [step, setStep] = useState<AITripPreferenceStep>('start')

  const [selectedThemeNames, setSelectedThemeNames] = useState<Array<string>>(EmptyArray)
  const [selectedMustDoActivityNames, toggleMustDoActivity] = useMultiValueToggle<string>(EmptyArray)

  const [tripPaces, setTripPaces] = useState<Array<TripPaceOption>>([
    { tripPace: 'Relaxed', selected: false },
    { tripPace: 'Balanced', selected: false },
    { tripPace: 'Active', selected: false },
  ])

  const [proximity, setProximity] = useState<Array<ProximityOption>>([
    { proximity: 'Keep it local', selected: false },
    { proximity: 'See the best within reach', selected: false },
    { proximity: 'Keen to get out and about', selected: false },
  ])

  const [foodStyle, setFoodStyle] = useState<Array<FoodStyleOption>>([
    { foodStyle: 'Keep It Simple', selected: false },
    { foodStyle: 'Casual Indulgence', selected: false },
    { foodStyle: 'Only the Best', selected: false },
    { foodStyle: 'Full-on Foodie', selected: false },
  ])

  const [activityBudget, setActivityBudget] = useState<Array<ActivityBudgetOption>>([
    { activityBudget: 'A Little Splash', selected: false },
    { activityBudget: 'Mix & Match', selected: false },
    { activityBudget: 'Open Wallet', selected: false },
  ])

  const [dietaryRequirements, setDietaryRequirements] = useState<Array<DietaryRequirementOption>>([
    { dietaryRequirement: 'Vegan', selected: false },
    { dietaryRequirement: 'Vegetarian', selected: false },
    { dietaryRequirement: 'Gluten-free', selected: false },
    { dietaryRequirement: 'Halal', selected: false },
  ])

  const [freeText, setFreeText] = useState('')

  const [location, setLocation] = useState('')
  const [locations, setLocations] = useState<Array<Locations>>([])
  const [occupancy, setOccupancy] = useState<OccupancyOption>(initialOccupancy || {
    adults: 2,
    children: [],
  })
  const [datePicker, setDatePicker] = useState<DatePickerOption>(() => {
    if (initialStartDate && initialEndDate && validateIsoDateFormat(initialStartDate) && validateIsoDateFormat(initialEndDate)) {
      return {
        startDate: new Date(initialStartDate),
        endDate: new Date(initialEndDate),
      }
    }
    // const startDate = getSecondTuesdayOfNextMonth()
    // const endDate = moment(startDate).add(4, 'days').toDate()
    return {
      startDate: undefined,
      endDate: undefined,
    }
  })

  const [shouldGenerateActivities, setShouldGenerateActivities] = useState<boolean | null>(true)
  const [analyticsType, setAnalyticsType] = useState<'customised' | 'non-customised'>('non-customised')
  const [analyticsUserChoices, setAnalyticsUserChoices] = useState<Array<string>>([])
  const [newTripId, setNewTripId] = useState<string | undefined>(undefined)

  const handleThemeToggle = useCallback((theme: string) => {
    setSelectedThemeNames(prev => {
      if (prev.includes(theme)) {
        return prev.filter(t => t !== theme)
      } else {
        return [...prev, theme]
      }
    })
  }, [])

  const handlePaceSelect = useCallback((pace: string) => {
    setTripPaces(prev => prev.map(p => ({
      ...p,
      selected: p.tripPace === pace,
    })))
  }, [])

  const handleProximitySelect = useCallback((proximity: string) => {
    setProximity(prev => prev.map(p => ({
      ...p,
      selected: p.proximity === proximity,
    })))
  }, [])

  const handleFoodStyleSelect = useCallback((foodStyle: string) => {
    setFoodStyle(prev => prev.map(f => ({
      ...f,
      selected: f.foodStyle === foodStyle ? !f.selected : false,
    })))
  }, [])

  const handleBudgetSelect = useCallback((budget: string) => {
    setActivityBudget(prev => prev.map(b => ({
      ...b,
      selected: b.activityBudget === budget,
    })))
  }, [])

  const handleLocationSelect = useCallback((location: string) => {
    setLocation(location)
  }, [])

  const handleOccupancySelect = useCallback((occupancy: OccupancyOption) => {
    setOccupancy(occupancy)
  }, [])

  const handleAddLocationParams = useCallback((params: {
    locationName: string;
    startDate?: Date;
    endDate?: Date;
    occupancy?: OccupancyOption;
    duration?: number;
    destinationId?: string
  }) => {
    const { locationName, startDate, endDate, occupancy, duration, destinationId } = params
    setLocations(prev => {
      const existingIndex = prev.findIndex(loc => loc.locationName === locationName)

      if (existingIndex !== -1) {
        const updatedLocations = [...prev]
        updatedLocations[existingIndex] = {
          ...updatedLocations[existingIndex],
          startDate: startDate ? moment(startDate).format(ISO_DATE_FORMAT) : updatedLocations[existingIndex].startDate,
          endDate: endDate ? moment(endDate).format(ISO_DATE_FORMAT) : updatedLocations[existingIndex].endDate,
          occupancy: occupancy || updatedLocations[existingIndex].occupancy,
          duration: duration || updatedLocations[existingIndex].duration,
          destinationId: destinationId || updatedLocations[existingIndex].destinationId,
        }
        return updatedLocations
      } else {
        const newLocations = [...prev, {
          locationName: locationName || '',
          startDate: startDate ? moment(startDate).format(ISO_DATE_FORMAT) : undefined,
          endDate: endDate ? moment(endDate).format(ISO_DATE_FORMAT) : undefined,
          occupancy,
          duration,
          destinationId,
        } as Locations]
        return newLocations
      }
    })
  }, [])

  const handleRemoveLocation = useCallback((locationName: string) => {
    setLocations(prev => {
      const locationIndex = prev.findIndex(loc => loc.locationName === locationName)
      if (locationIndex === -1) return prev

      const remainingLocations = prev.filter(loc => loc.locationName !== locationName)

      if (datePicker.startDate && datePicker.endDate && remainingLocations.length > 0) {
        const totalDays = calculateTripDays(datePicker.startDate, datePicker.endDate)
        const durations = distributeDaysEvenly(totalDays, remainingLocations.length)
        return updateLocationsWithDurations(remainingLocations, datePicker.startDate, durations)
      }

      return remainingLocations
    })
  }, [datePicker.startDate, datePicker.endDate])

  const handleUpdateAllLocationsOccupancy = useCallback((occupancy: OccupancyOption) => {
    setLocations(prev => {
      const updatedLocations = prev.map(loc => ({
        ...loc,
        occupancy: {
          adults: occupancy.adults,
          children: occupancy.children,
        },
      }))
      return updatedLocations
    })
  }, [])

  const handleLocationsReorder = useCallback((newLocations: Array<Locations>) => {
    if (datePicker.startDate && newLocations.length > 0) {
      const durations = newLocations.map(loc => loc.duration || 1)
      const updatedLocations = updateLocationsWithDurations(newLocations, datePicker.startDate, durations)
      setLocations(updatedLocations)
    } else {
      setLocations(newLocations)
    }
  }, [datePicker.startDate])

  const handleLocationDurationChange = useCallback((locationIndex: number, newDuration: number) => {
    if (datePicker.startDate) {
      const { updatedLocations, newTripEndDate } = updateLocationDuration(
        locations,
        locationIndex,
        newDuration,
        datePicker.startDate,
      )

      setLocations(updatedLocations)
      setDatePicker(prev => ({
        ...prev,
        endDate: newTripEndDate,
      }))
    }
  }, [locations, datePicker.startDate])

  const handleDatePickerSelect = useCallback((datePicker: DatePickerOption) => {
    setDatePicker(datePicker)

    if (datePicker.startDate && datePicker.endDate && locations.length > 0) {
      const totalDays = calculateTripDays(datePicker.startDate, datePicker.endDate)
      const durations = distributeDaysEvenly(totalDays, locations.length)
      const updatedLocations = updateLocationsWithDurations(locations, datePicker.startDate, durations)
      setLocations(updatedLocations)
    }
  }, [locations])

  const handleDietaryRequirementSelect = useCallback((dietaryRequirement: string) => {
    setDietaryRequirements(prev => {
      if (dietaryRequirement.startsWith('Custom:')) {
        const filteredRequirements = prev.filter(d => !d.dietaryRequirement.startsWith('Custom:'))
        return [...filteredRequirements, { dietaryRequirement, selected: true }]
      }
      return prev.map(d => ({
        ...d,
        selected: d.dietaryRequirement === dietaryRequirement ? !d.selected : d.selected,
      }))
    })
  }, [])

  const isAnyThemeSelected = selectedThemeNames.length > 0
  const isAnyMustDoActivitySelected = selectedMustDoActivityNames.length > 0
  const isAnyFoodStyleOrDietRequirementSelected = dietaryRequirements.some(d => d.selected) || foodStyle.some(f => f.selected)

  return {
    step,
    selectedThemeNames,
    selectedMustDoActivityNames,
    tripPaces,
    proximity,
    foodStyle,
    activityBudget,
    dietaryRequirements,
    freeText,
    location,
    locations,
    occupancy,
    datePicker,
    shouldGenerateActivities,
    analyticsType,
    analyticsUserChoices,
    newTripId,
    setStep,
    setSelectedThemeNames,
    setTripPaces,
    setProximity,
    setFoodStyle,
    setActivityBudget,
    setDietaryRequirements,
    setFreeText,
    setShouldGenerateActivities,
    setAnalyticsType,
    setAnalyticsUserChoices,
    setNewTripId,
    handleThemeToggle,
    handlePaceSelect,
    handleProximitySelect,
    handleFoodStyleSelect,
    handleBudgetSelect,
    handleDietaryRequirementSelect,
    toggleMustDoActivity,
    isAnyThemeSelected,
    isAnyMustDoActivitySelected,
    isAnyFoodStyleOrDietRequirementSelected,
    handleLocationSelect,
    handleOccupancySelect,
    handleAddLocationParams,
    handleRemoveLocation,
    handleUpdateAllLocationsOccupancy,
    handleLocationsReorder,
    handleDatePickerSelect,
    handleLocationDurationChange,
  }
}
